/* Background/hero video playback + rendition picking.
   Replaces the per-section IntersectionObserver blocks that used to live inline in
   hero-banner, big-stat, image-with-text, hsa-landing, video-gallery, video-steps and
   use-case. Markup comes from snippets/bg-video.liquid. */
(function () {
  'use strict';

  // snippets/bg-video.liquid emits the tag once per video rather than touching
  // theme.liquid, so guard against running the whole thing several times per page.
  if (window.__saltivateBgVideo) { return; }
  window.__saltivateBgVideo = true;

  var VIDS = 'video[data-autoplay]';

  function ladder(v) {
    var raw = v.getAttribute('data-vsrc');
    if (!raw) { return null; }
    try {
      var arr = JSON.parse(raw);
      if (!arr || !arr.length) { return null; }
      return arr;
    } catch (e) { return null; }
  }

  // Browsers pick the FIRST playable <source> and ignore its dimensions, so a list of
  // same-mime renditions buys nothing. Choosing here is what keeps a phone off the 1080p
  // file: the smallest rendition that still covers the element at its rendered size.
  // These are decorative clips behind a scrim, so 720p is the ceiling worth paying for:
  // above it the file roughly doubles for detail the wash hides. Older uploads still carry
  // a 1080p rendition, and the height slack below would otherwise select it on exactly the
  // tall, narrow phone viewports we least want it on.
  var MAX_H = 720;

  function pick(list, el) {
    var sources = list.slice().sort(function (a, b) { return (a.w * a.h) - (b.w * b.h); });
    // Keep at least one source if a clip somehow has nothing at or below the ceiling.
    var capped = sources.filter(function (s) { return s.h <= MAX_H; });
    if (capped.length) { sources = capped; }
    else { sources = sources.slice(0, 1); }
    var conn = navigator.connection || navigator.mozConnection || {};
    if (conn.saveData) { return sources[0]; }
    // A slow link costs more in stall time than the sharpness is worth. The clip hides
    // under a colour wash either way, but a poster frame that never resolves reads to the
    // visitor as a broken page rather than a restrained one.
    var et = conn.effectiveType || '';
    if (et === 'slow-2g' || et === '2g' || et === '3g') { return sources[0]; }
    // Capped at 1.5: a 3x phone would otherwise demand more pixels than the 1080p master
    // has, for a background clip sitting under a colour wash. Phone widths cap at 1x on
    // top of that, because those viewports are portrait and the cropped vertical axis is
    // what drives the fit test below. At 1.5 a 413px-wide hero asks for 831 vertical
    // pixels, the 480p rendition misses on 831/480 = 1.73 against the 1.3 slack, and the
    // phone climbs to the 720p file: 2.7MB on the connection least able to carry it. At
    // 1x the same hero asks for 554, 480p clears the slack at 1.15, and it costs 1.5MB.
    // Nothing visible is given up; the element is 413 CSS px wide behind a scrim.
    var dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 750 ? 1 : 1.5);
    var rect = el.getBoundingClientRect();
    var needW = (rect.width || window.innerWidth) * dpr;
    var needH = (rect.height || 0) * dpr;
    for (var i = 0; i < sources.length; i++) {
      var s = sources[i];
      // Width is the axis a full-bleed hero actually stretches, and softness there is
      // what reads as "low resolution", so never upscale horizontally.
      if (s.w < needW) { continue; }
      // The other axis gets cropped away by object-fit: cover, so it can stretch a
      // little. Without this a phone hero, being tall and narrow, demands more vertical
      // pixels than any rendition has and every device ends up on the 1080p file.
      if (Math.max(needW / s.w, needH / s.h) <= 1.3) { return s; }
    }
    return sources[sources.length - 1];
  }

  function load(v) {
    if (v.dataset.vloaded) { return; }
    v.dataset.vloaded = '1';
    var srcEl = v.querySelector('source');
    if (!srcEl) { return; }
    var url = srcEl.getAttribute('data-src');
    var type = 'video/mp4';
    var all = ladder(v) || [];

    // Shopify publishes an HLS playlist alongside the MP4s and adapts it to bandwidth on
    // its own, which beats a fixed choice once playback is running. Safari and iOS play it
    // natively. Chrome and Firefox cannot and would stall on it, so they get the MP4.
    //
    // Phones are the exception, and it is the whole reason the hero read as "never loads"
    // on an iPhone. Getting to the first frame over HLS costs two extra round trips (master
    // playlist, then variant playlist, then segments), and Shopify lists the 720p variant
    // FIRST in the master, which is where a player with no bandwidth estimate starts. So the
    // phone opened with the heaviest stream after the slowest handshake. These are short
    // decorative loops and the ladder already tells us which rendition fits, so below the
    // phone breakpoint take the picked MP4: one request, and pick() caps a phone at 480p.
    var hls = null;
    for (var i = 0; i < all.length; i++) {
      if (all[i].f === 'm3u8') { hls = all[i]; break; }
    }
    var wantHls = hls && window.innerWidth >= 750;
    if (wantHls && v.canPlayType('application/vnd.apple.mpegurl')) {
      url = hls.url;
      type = hls.t || 'application/vnd.apple.mpegurl';
    } else {
      var mp4s = all.filter(function (s) { return s.f === 'mp4'; });
      if (mp4s.length) {
        var chosen = pick(mp4s, v);
        if (chosen) { url = chosen.url; type = chosen.t || 'video/mp4'; }
      }
    }

    if (!url) { return; }
    srcEl.setAttribute('type', type);
    srcEl.setAttribute('src', url);
    v.load();
  }

  // Built in script rather than in bg-video.liquid because it exists only under a media
  // query Liquid cannot see. The styles are inline for a related reason: Shopify subsets
  // {% stylesheet %} per rendered file, so a class that only JavaScript ever emits loses
  // its rules on every page where the defining file does not itself render.
  //
  // It sits in the bottom corner, not centred: these clips run full bleed behind a
  // headline and CTAs, and a 52px disc in the middle lands on top of the copy.
  function addPlayControl(v) {
    if (v.dataset.vplay) { return; }
    v.dataset.vplay = '1';
    var host = v.parentElement;
    if (!host) { return; }
    // Every caller positions the media wrapper already. Promote a static one rather than
    // let the control escape to some ancestor halfway up the page.
    if (getComputedStyle(host).position === 'static') { host.style.position = 'relative'; }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Play video');
    btn.style.cssText = 'position:absolute;right:var(--space-3);bottom:var(--space-3);' +
      'width:52px;height:52px;border:0;border-radius:999px;background:rgba(255,255,255,.92);' +
      'display:grid;place-items:center;cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,.25);' +
      'z-index:2';

    var tri = document.createElement('span');
    tri.style.cssText = 'width:0;height:0;margin-left:var(--space-2);' +
      'border-left:15px solid var(--color-navy);border-top:9px solid transparent;' +
      'border-bottom:9px solid transparent';
    btn.appendChild(tri);

    btn.addEventListener('click', function () {
      load(v);
      // The preference is about indefinite looping, not about the one clip the visitor
      // just asked to see, so it plays through once and the control comes back.
      v.loop = false;
      var p = v.play();
      if (p) { p.catch(function () { btn.style.display = ''; }); }
      btn.style.display = 'none';
    });
    v.addEventListener('ended', function () {
      v.currentTime = 0;
      btn.style.display = '';
    });

    host.appendChild(btn);
  }

  function observe(root) {
    var vids = (root || document).querySelectorAll(VIDS);
    if (!vids.length) { return; }
    // WCAG 2.2.2: these loop indefinitely, so a reduced-motion preference leaves them
    // parked on the poster frame and never fetches the file on its own. What 2.2.2 asks
    // for is a way to start the motion, not that the clip be unreachable, so each one
    // gets a play control instead. Reduce Motion is commonly on for phones and off on
    // desktop, which is why this read as "the video is broken on mobile".
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      Array.prototype.forEach.call(vids, addPlayControl);
      return;
    }
    if (!('IntersectionObserver' in window)) { return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          load(v);
          var p = v.play();
          if (p) { p.catch(function () {}); }
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.2 });
    Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
  }

  function init() { observe(document); }

  // Sections that drive playback themselves (layered-slideshow plays only the active
  // slide) still need the rendition choice, so hand them the resolver.
  window.SaltivateBgVideo = { load: load };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { observe(e.target); });
})();
